import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Plus, Edit2, Trash2, X, Search } from 'lucide-react';
import { collection, getDocs, query, orderBy, limit, addDoc, updateDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useBlogPostsAggregate } from '../utils/useBlogPostsAggregate';
import { updateBlogPostsAggregationOnCreate, updateBlogPostsAggregationOnUpdate, updateBlogPostsAggregationOnDelete } from '../utils/blogPostsAggregations';
import Layout from '../components/Layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import useConfirmation from '../hooks/useConfirmation';
import ConfirmationModal from '../components/ConfirmationModal';
import { BlogStructuredData } from '../components/StructuredData';
import { backfillBlogPostsAggregate } from '../utils/backfillAggregations';
import useSEO from '../hooks/useSEO';
import BlogPostEditorModal from '../components/BlogPostEditorModal';

function Blog() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { confirmation, showConfirmation, confirmAction, cancelAction } = useConfirmation();
  const [showPostModal, setShowPostModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [saving, setSaving] = useState(false);
  const [postForm, setPostForm] = useState({
    titulo: '',
    resumo: '',
    conteudo: '',
    urlImagem: '',
    etiquetas: ''
  });
  const [searchTerm, setSearchTerm] = useState('');

  // Usar agregado de posts do blog (atualização em tempo real)
  const { posts, loading } = useBlogPostsAggregate();

  // SEO dinâmico
  useSEO({
    title: 'Blog',
    description: 'Blog Aruk Dog: dicas, cuidados e novidades sobre o mundo pet. Conteúdo sobre banho, tosa e cuidados para seu pet.',
    canonical: '/blog',
    ogType: 'website',
    ogImage: '/Preto.png'
  });

  const formatDate = (date) => {
    if (!date) return '';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatReadingTime = (minutes) => {
    if (!minutes) return '2 min';
    return `${minutes} min de leitura`;
  };

  const handleOpenPostModal = useCallback((post = null) => {
    if (post) {
      setEditingPost(post);
      setPostForm({
        titulo: post.titulo || post.title || '',
        resumo: post.resumo || post.excerpt || '',
        conteudo: post.conteudo || post.content || post.body || '',
        urlImagem: post.urlImagem || post.imageUrl || '',
        etiquetas: (post.etiquetas || post.tags) ? (post.etiquetas || post.tags).join(', ') : ''
      });
    } else {
      setEditingPost(null);
      setPostForm({
        titulo: '',
        resumo: '',
        conteudo: '',
        urlImagem: '',
        etiquetas: ''
      });
    }
    setShowPostModal(true);
  }, []);

  const handleClosePostModal = useCallback(() => {
    setShowPostModal(false);
    setEditingPost(null);
    setPostForm({
      titulo: '',
      resumo: '',
      conteudo: '',
      urlImagem: '',
      etiquetas: ''
    });
  }, []);

  const handleSavePost = async () => {
    if (!postForm.titulo.trim()) {
      await showConfirmation({
        title: 'Campo obrigatório',
        message: 'O título é obrigatório.',
        confirmText: 'OK',
        showCancel: false,
        type: 'warning'
      });
      return;
    }

    const confirmSave = await showConfirmation({
      title: editingPost ? 'Salvar alterações' : 'Publicar post',
      message: editingPost
        ? 'Deseja salvar as alterações neste post?'
        : 'Deseja publicar este novo post?',
      confirmText: editingPost ? 'Salvar' : 'Publicar',
      cancelText: 'Cancelar',
      type: 'warning'
    });

    if (!confirmSave) return;

    setSaving(true);
    try {
      const etiquetasArray = postForm.etiquetas
        ? postForm.etiquetas.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
        : [];

      const postData = {
        titulo: postForm.titulo.trim(),
        resumo: postForm.resumo.trim() || '',
        conteudo: postForm.conteudo.trim() || '',
        urlImagem: postForm.urlImagem.trim() || '',
        etiquetas: etiquetasArray,
        atualizadoEm: Timestamp.now()
      };

      if (editingPost) {
        // Atualizar post existente
        const updatedPostData = {
          ...postData,
          criadoEm: editingPost.criadoEm instanceof Date 
            ? Timestamp.fromDate(editingPost.criadoEm)
            : editingPost.criadoEm
        };
        
        await updateDoc(doc(db, 'postsBlog', editingPost.id), updatedPostData);
        
        // Otimização: usar dados que já temos ao invés de ler do Firestore
        const postDataForAggregate = {
          id: editingPost.id,
          ...updatedPostData
        };
        
        // Atualizar agregado
        try {
          await updateBlogPostsAggregationOnUpdate(postDataForAggregate);
        } catch (err) {
          // Erro ao atualizar agregado
          // Erro ao atualizar agregado ao editar post
        }
        
        // O listener em tempo real atualizará automaticamente
        
        await showConfirmation({
          title: 'Sucesso!',
          message: '',
          confirmText: 'OK',
          showCancel: false
        });
      } else {
        // Criar novo post
        const createdAt = Timestamp.now();
        const docRef = await addDoc(collection(db, 'postsBlog'), {
          ...postData,
          criadoEm: createdAt
        });
        
        // Otimização: usar dados que já temos ao invés de ler do Firestore
        const newPost = {
          id: docRef.id,
          ...postData,
          criadoEm: createdAt
        };
        
        // Atualizar agregado
        try {
          await updateBlogPostsAggregationOnCreate(newPost);
        } catch (err) {
          // Erro ao atualizar agregado
          // Erro ao atualizar agregado ao criar post
        }
        
        // O listener em tempo real atualizará automaticamente
      }

      handleClosePostModal();
    } catch (error) {
      await showConfirmation({
        title: 'Erro',
        message: 'Ocorreu um erro ao salvar o post. Por favor, tente novamente.',
        confirmText: 'OK',
        showCancel: false,
        type: 'danger'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePost = async (post) => {
    const confirmed = await showConfirmation({
      title: 'Excluir Post',
      message: 'Tem certeza que deseja excluir este post? Esta ação não pode ser desfeita.',
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      type: 'danger'
    });

    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, 'postsBlog', post.id));
      
      // Atualizar agregado
      try {
        await updateBlogPostsAggregationOnDelete(post.id);
      } catch (err) {
        // Erro ao atualizar agregado
        // Erro ao atualizar agregado ao excluir post
      }
      
      // O listener em tempo real atualizará automaticamente
      
      await showConfirmation({
        title: 'Sucesso!',
        message: '',
        confirmText: 'OK',
        showCancel: false
      });
    } catch (error) {
      await showConfirmation({
        title: 'Erro',
        message: 'Ocorreu um erro ao excluir o post. Por favor, tente novamente.',
        confirmText: 'OK',
        showCancel: false,
        type: 'danger'
      });
    }
  };

  const handleFormChange = useCallback((field, value) => {
    setPostForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleBackfill = async () => {
    const confirmed = await showConfirmation({
      title: 'Executar Backfill',
      message: 'Tem certeza que deseja executar o backfill do agregado de posts? Isso irá recriar o agregado com todos os posts existentes.',
      confirmText: 'Executar',
      cancelText: 'Cancelar',
      type: 'warning'
    });

    if (!confirmed) return;

    try {
      setSaving(true);
      const result = await backfillBlogPostsAggregate();
      
      if (result.success) {
        await showConfirmation({
          title: 'Sucesso!',
          message: '',
          confirmText: 'OK',
          showCancel: false
        });
        
        // O listener em tempo real atualizará automaticamente
      } else {
        throw new Error(result.error || 'Erro desconhecido');
      }
    } catch (error) {
      await showConfirmation({
        title: 'Erro',
        message: `Ocorreu um erro ao executar o backfill: ${error.message || 'Erro desconhecido'}`,
        confirmText: 'OK',
        showCancel: false,
        type: 'danger'
      });
    } finally {
      setSaving(false);
    }
  };

  // Filtrar posts baseado no termo de pesquisa
  const filteredPosts = useMemo(() => {
    if (!searchTerm || searchTerm.trim() === '') {
      return posts;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    return posts.filter(post => {
      const titulo = ((post.titulo || post.title) || '').toLowerCase();
      const resumo = ((post.resumo || post.excerpt) || '').toLowerCase();
      const conteudo = ((post.conteudo || post.content) || '').toLowerCase();
      const etiquetas = ((post.etiquetas || post.tags) || []).join(' ').toLowerCase();
      
      return titulo.includes(searchLower) || 
             resumo.includes(searchLower) || 
             conteudo.includes(searchLower) ||
             etiquetas.includes(searchLower);
    });
  }, [posts, searchTerm]);

  const BlogContent = (
    <div className="min-h-screen bg-background-light dark:bg-background-dark">
      <BlogStructuredData posts={posts} />
      {/* Header */}
      <div 
        className="text-white py-6 sm:py-8 px-4 overflow-hidden"
        style={{ 
          backgroundColor: '#104347',
          backgroundImage: 'radial-gradient(circle, rgba(255, 255, 255, 0.15) 1px, transparent 1px)',
          backgroundSize: '30px 30px'
        }}
      >
        <div className="max-w-6xl mx-auto">
          <div className="flex items-start justify-between mb-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-white/90 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5 shrink-0" />
              <span className="sm:hidden">Voltar</span>
              <span className="hidden sm:inline">Voltar para a página inicial</span>
            </button>
            {user && (
              <button
                onClick={() => handleOpenPostModal()}
                className="flex items-center gap-2 px-4 py-2 bg-white text-primary-600 rounded-lg hover:bg-white/90 transition-colors font-medium"
              >
                <Plus className="h-5 w-5 shrink-0" />
                <span className="sm:hidden">Novo</span>
                <span className="hidden sm:inline">Novo Post</span>
              </button>
            )}
          </div>
          <div className="text-center space-y-3">
            <div className="flex justify-center -mb-8">
              <img 
                src="/Branco.png" 
                alt="Aruk Dog Logo" 
                className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40"
              />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              Blog Aruk Dog
            </h1>
            <p className="text-base sm:text-lg text-white/90 max-w-2xl mx-auto mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
              Dicas, cuidados e novidades sobre o mundo pet
            </p>
            
            {/* Barra de Pesquisa */}
            <div className="max-w-3xl mx-auto relative mt-6">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/70 z-10" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Pesquisar no blog..."
                  className="w-full pl-12 pr-4 py-2 text-base border-2 border-white/30 rounded-full focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 bg-white/10 backdrop-blur-sm text-white placeholder-white/70 shadow-lg relative z-0"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white/70 hover:text-white transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Posts Grid */}
      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
            <p className="text-muted-light dark:text-muted-dark">Carregando posts...</p>
          </div>
        ) : filteredPosts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {filteredPosts.map((post) => (
              <article
                key={post.id}
                className="bg-card-light dark:bg-card-dark rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 border border-border-light dark:border-border-dark cursor-pointer"
                onClick={() => navigate(`/blog/${post.id}`)}
              >
                  {(post.urlImagem || post.imageUrl) && (
                    <div className="aspect-video overflow-hidden bg-gray-200 dark:bg-gray-700">
                      <img
                        src={post.urlImagem || post.imageUrl}
                        alt={post.titulo || post.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-muted-light dark:text-muted-dark mb-3">
                      {post.criadoEm && (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(post.criadoEm)}</span>
                        </div>
                      )}
                      {post.readingTime && (
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>{formatReadingTime(post.readingTime)}</span>
                        </div>
                      )}
                    </div>
                    
                    <h2 className="text-xl font-bold text-text-light dark:text-text-dark mb-2 line-clamp-2">
                      {post.titulo || post.title}
                    </h2>
                    
                    {(post.resumo || post.excerpt) && (
                      <p className="text-muted-light dark:text-muted-dark mb-4 line-clamp-3">
                        {post.resumo || post.excerpt}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 font-medium">
                      <span>Ler mais</span>
                      <ArrowLeft className="w-4 h-4 rotate-180" />
                    </div>
                  </div>
              </article>
            ))}
          </div>
        ) : searchTerm ? (
          <div className="text-center py-20">
            <p className="text-xl text-muted-light dark:text-muted-dark mb-4">
              Nenhum post encontrado para "{searchTerm}"
            </p>
            <p className="text-muted-light dark:text-muted-dark">
              Tente pesquisar com outros termos.
            </p>
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-xl text-muted-light dark:text-muted-dark mb-4">
              Nenhum post encontrado
            </p>
            <p className="text-muted-light dark:text-muted-dark">
              Em breve teremos conteúdo interessante para você!
            </p>
          </div>
        )}
      </div>

      {/* Modal de Criar/Editar Post */}
      <BlogPostEditorModal
        isOpen={showPostModal}
        editingPost={editingPost}
        postForm={postForm}
        onClose={handleClosePostModal}
        onSave={handleSavePost}
        onFormChange={handleFormChange}
        saving={saving}
      />

      {/* Modal de Confirmação */}
      <ConfirmationModal
        isOpen={confirmation.isOpen}
        title={confirmation.title}
        message={confirmation.message}
        confirmText={confirmation.confirmText}
        cancelText={confirmation.cancelText}
        type={confirmation.type}
        showCancel={confirmation.showCancel}
        onConfirm={confirmAction}
        onCancel={cancelAction}
      />
    </div>
  );

  // Se o usuário está logado, mostrar com Layout
  if (user) {
    return (
      <Layout>
        {BlogContent}
      </Layout>
    );
  }

  // Se não está logado, mostrar sem Layout
  return BlogContent;
};

export default Blog;

